import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Process.css";
import fig1 from "@/assets/process/fig1.jpg";
import fig2 from "@/assets/process/fig2.jpg";
import fig3 from "@/assets/process/fig3.jpg";
import fig4 from "@/assets/process/fig4.jpg";

// The Method — editorial process page. Header/ticker/footer come from Layout.
export default function Process() {
  useEffect(() => {
    const els = document.querySelectorAll(".pp .reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((e) => e.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  return (
    <div className="pp">
      <section className="hero">
        <div className="wrap hero__grid">
          <div className="reveal">
            <p className="eyebrow">The Method</p>
            <h1>We don't list<br />what we haven't<br /><em>inspected.</em></h1>
            <p className="lede hero__copy">Every product on The Spotters starts with a physical sample. Before it reaches our marketplace, it passes through three independent checks — authenticity, quality, and fit&nbsp;&amp;&nbsp;comfort.</p>
          </div>
          <div className="reveal">
            <figure className="plate plate--photo" style={{margin: '0'}}>
              <img src={fig1} alt="The Spotters inspection — authenticity (loupe), quality (caliper), fit & comfort (heel), and the reference protocol" />
              <figcaption className="plate__cap"><span>Fig. 01</span><span>The four checks, in hand</span></figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="origin">
        <div className="wrap reveal">
          <p className="eyebrow">Where we start</p>
          <h2>Most marketplaces start with a listing.<br /><span className="accent">We start with the product.</span></h2>
          <p className="lede sub">Because our process literally begins with one instruction to the seller.</p>
          <div className="origin__pair">
            <div className="origin__card is-them">
              <p className="k">The rest of the market</p>
              <p className="instr"><s>"Upload your catalogue."</s></p>
            </div>
            <div className="origin__card is-us">
              <p className="k">The Spotters</p>
              <p className="instr">"Send us the physical product."</p>
            </div>
          </div>
          <p className="origin__foot">We don't start with the seller's catalogue. We start with their <em>product</em>.</p>
        </div>
      </section>

      <section className="statement">
        <div className="wrap reveal">
          <h2>Before you can buy it,<br />we've already seen it.</h2>
          <p className="lede sub">Four stages stand between a vendor's sample and a listing. None of them are photographs.</p>
        </div>
      </section>

      <section className="wrap">
  
        <div className="stage reveal">
          <div>
            <div className="stage__num"><b>01</b><span className="k">Stage · Sample Submitted</span></div>
            <h3>The vendor sends a physical sample.</h3>
            <div className="stage__body">
              <span className="beat">Not photographs.</span>
              <span className="beat">Not a catalogue image.</span>
              <span className="beat"><strong>The actual product</strong>, in hand, on the bench.</span>
            </div>
          </div>
          <figure className="plate plate--photo" style={{margin: '0'}}>
            <img src={fig2} alt="Sample intake: logged, opened, weighed, and filed as reference" />
            <figcaption className="plate__cap"><span>Fig. 02</span><span>Sample intake</span></figcaption>
          </figure>
        </div>

  
        <div className="stage reveal">
          <div>
            <div className="stage__num"><b>02</b><span className="k">Stage · The Three Spotters</span></div>
            <h3>The sample is examined by three perspectives.</h3>
            <div className="stage__body">One sample. Three independent reads — each looking for a different kind of failure.</div>
            <div className="trio">
              <div><p className="role accent">The Skeptic</p><p className="disc sk">Authenticity</p></div>
              <div><p className="role accent">The Perfectionist</p><p className="disc sk">Quality</p></div>
              <div><p className="role accent">The Softie</p><p className="disc sk">Fit &amp; Comfort</p></div>
            </div>
          </div>
          <figure className="plate plate--photo" style={{margin: '0'}}>
            <img src={fig3} alt="Bench examination: material, construction, wear, and final verdict" />
            <figcaption className="plate__cap"><span>Fig. 03</span><span>On the bench</span></figcaption>
          </figure>
        </div>

  
        <div className="stage reveal">
          <div>
            <div className="stage__num"><b>03</b><span className="k">Stage · The Sign-Off</span></div>
            <h3>All three have to agree.</h3>
            <div className="stage__body">If one fails, the product doesn't get listed. There is no partial pass, no override, no exception for a good story.</div>
          </div>
          <div>
            <div className="mini-rule">
              <q>"If one Spotter says no, the answer is no."</q>
              <p className="by">— The sign-off</p>
            </div>
          </div>
        </div>

  
        <div className="stage reveal">
          <div>
            <div className="stage__num"><b>04</b><span className="k">Stage · The Reference</span></div>
            <h3>The approved sample becomes the reference standard.</h3>
            <div className="stage__body">Kept on file, it tells us exactly what that product is <strong>supposed to be</strong> — the benchmark every future shipment is measured against.</div>
          </div>
          <figure className="plate plate--photo" style={{margin: '0'}}>
            <img src={fig4} alt="The reference standard: cataloging, benchmark repetition, consistency checks, and the unified approved verdict" />
            <figcaption className="plate__cap"><span>Fig. 04</span><span>The reference standard</span></figcaption>
          </figure>
        </div>
      </section>

      <section className="order">
        <div className="wrap reveal">
          <p className="eyebrow">Then you order</p>
          <h2>Then you order.</h2>
          <p className="lede" style={{marginTop: '24px'}}>Once a product has earned its place on The Spotters, it becomes available to you — and it ships the short way.</p>
          <div className="flow" role="img" aria-label="You, order, approved vendor, direct dispatch, your door">
            <span className="node you">You</span><span className="arw">→</span>
            <span className="node">Order</span><span className="arw">→</span>
            <span className="node">Approved vendor</span><span className="arw">→</span>
            <span className="node">Direct dispatch</span><span className="arw">→</span>
            <span className="node you">Your door</span>
          </div>
          <p className="order__payoff"><span>Seen.</span><span>Approved.</span><span className="accent">Yours.</span></p>
        </div>
      </section>

      <section className="flows">
        <div className="wrap reveal">
          <p className="eyebrow" style={{marginBottom: '36px'}}>Two flows, never crossed</p>
          <div className="flowrow">
            <span className="lab">Product approval</span>
            <div className="flow">
              <span className="node">Vendor</span><span className="arw">&rarr;</span>
              <span className="node">Physical sample</span><span className="arw">&rarr;</span>
              <span className="node">The Spotters</span><span className="arw">&rarr;</span>
              <span className="node">Approved reference</span><span className="arw">&rarr;</span>
              <span className="node node--end">Listed</span>
            </div>
          </div>
          <div className="flowrow">
            <span className="lab">Customer fulfilment</span>
            <div className="flow">
              <span className="node">Customer</span><span className="arw">&rarr;</span>
              <span className="node">Order</span><span className="arw">&rarr;</span>
              <span className="node">Approved vendor</span><span className="arw">&rarr;</span>
              <span className="node">Direct dispatch</span><span className="arw">&rarr;</span>
              <span className="node node--end">Customer</span>
            </div>
          </div>
          <p className="flows__punch">We don't inspect after something goes wrong.<br /><span className="accent">We inspect before you buy.</span></p>
        </div>
      </section>

      <section className="invert">
        <div className="wrap reveal">
          <p className="eyebrow">Accountability</p>
          <h2>Direct from the vendor.<br />Held to our standard.</h2>
          <div className="split">
            <div>
              <h5>The vendor</h5>
              <ul><li>Provides the product</li><li>Packs your order</li><li>Ships directly to you</li></ul>
            </div>
            <div>
              <h5>The Spotters</h5>
              <ul><li>Sets the standard</li><li>Protects the standard</li><li>Stays accountable to you</li></ul>
            </div>
          </div>
        </div>
      </section>

      <section className="standard">
        <div className="wrap reveal">
          <p className="eyebrow">The Standard</p>
          <h2>Passing once<br />isn't enough.</h2>
          <p className="lede" style={{marginTop: '24px'}}>The first sample gets a product listed. Consistency is what keeps it there.</p>
          <div className="cards">
            <div><p className="n">01</p><h3>Authenticity</h3><p>Are the products genuine — every batch, not just the sample?</p></div>
            <div><p className="n">02</p><h3>Quality</h3><p>Does the supplied product match the approved standard on file?</p></div>
            <div><p className="n">03</p><h3>Consistency</h3><p>Does the vendor keep delivering what was approved, shipment after shipment?</p></div>
            <div><p className="n">04</p><h3>Customer experience</h3><p>Are customers actually receiving what they ordered?</p></div>
          </div>
        </div>
      </section>

      <section className="seal-wrap">
        <div className="wrap reveal">
          <div className="seal">
            <p className="k">The House Rule</p>
            <q>If it doesn't pass all three Spotters, it doesn't get listed.</q>
            <p className="by">— The Spotters</p>
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="wrap reveal">
          <h2>See what<br />we've spotted.</h2>
          <div className="cta__btns">
            <Link to="/shop" className="btn btn--solid">Enter the shop <span className="a">→</span></Link>
            <Link to="/spotters" className="btn btn--ghost">Meet the Spotters <span className="a">→</span></Link>
          </div>
          <p className="cta__foot">Every product has a story. <span>We just make sure it's worth telling.</span></p>
        </div>
      </section>
    </div>
  );
}
